/*
Copyright (C) 2026  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import Api from "@src/utils/ApiCaller";
import configLoader from "@src/utils/Config";

import type {
  DriverPlan,
  GuestAccess,
  GuestCheckResult,
} from "@src/@types/WizardData";
import type { Instance } from "@src/@types/Instance";

export const instanceKey = (instance: Instance) =>
  instance.instance_name || instance.id;

/** The addresses entered on the Guest access page, without empty ones. */
export const enteredAddresses = (access: GuestAccess): { [instance: string]: string } =>
  Object.fromEntries(
    Object.entries(access.addresses || {})
      .map(([k, v]) => [k, (v || "").trim()])
      .filter(([, v]) => v),
  );

// Credentials go to Barbican like endpoint secrets; only the reference is
// stored with the transfer.
const barbicanPayload = (data: any) => ({
  payload: JSON.stringify(data),
  payload_content_type: "text/plain",
  algorithm: "aes",
  bit_length: 256,
  mode: "cbc",
  content_types: { default: "text/plain" },
});

const credentials = (access: GuestAccess) => ({
  windows: {
    username: access.windows.username,
    password: access.windows.password,
    port: access.windows.port || (access.windows.https ? 5986 : 5985),
    https: Boolean(access.windows.https),
  },
  linux: {
    username: access.linux.username,
    // An SSH private key replaces the password when the admin signs in with a key.
    password: access.linux.pkey ? "" : access.linux.password,
    ...(access.linux.pkey ? { pkey: access.linux.pkey } : {}),
    port: access.linux.port || 22,
  },
});

class GuestAccessSource {
  async check(opts: {
    endpointId: string;
    instances: Instance[];
    access: GuestAccess;
    targetPlatform: string;
  }): Promise<{ results: GuestCheckResult[]; ready: boolean; drivers?: DriverPlan | null }> {
    const response = await Api.send({
      url: `${configLoader.config.servicesUrls.coriolis}/${Api.projectId}/endpoints/${opts.endpointId}/actions`,
      method: "POST",
      data: {
        "check-guest-access": {
          instances: opts.instances.map(instanceKey),
          os_types: opts.access.osTypes,
          guest_addresses: enteredAddresses(opts.access),
          credentials: credentials(opts.access),
          target_platform: opts.targetPlatform,
        },
      },
      timeout: 170000,
    });
    return response.data["check-guest-access"];
  }

  // Applies a check's fix, e.g. allowing the management port from the
  // Coriolis host in the VM's cloud firewall.
  async fix(opts: {
    endpointId: string;
    instance: string;
    fix: { action: string; port?: number };
  }): Promise<{ opened: boolean; detail: string }> {
    const response = await Api.send({
      url: `${configLoader.config.servicesUrls.coriolis}/${Api.projectId}/endpoints/${opts.endpointId}/actions`,
      method: "POST",
      data: {
        "check-guest-access": {
          action: opts.fix.action,
          instances: [opts.instance],
          port: opts.fix.port,
        },
      },
      timeout: 170000,
    });
    return response.data["check-guest-access"];
  }

  async storeCredentials(access: GuestAccess): Promise<string> {
    const response = await Api.send({
      url: `${configLoader.config.servicesUrls.barbican}/v1/secrets`,
      method: "POST",
      data: barbicanPayload(credentials(access)),
    });
    return response.data.secret_ref;
  }
}

export default new GuestAccessSource();
